import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Translate } from "react-thunk-i18nify";

import { singleDebtorFetchRequest } from "redux/actions/debtor";

import AgreementIndex from "views/Agreement/AgreementIndex";
import InvoiceIndex from "views/Invoice/InvoiceIndex";
import ContactIndex from "views/Contact/ContactIndex";
import PbxIndex from "views/Pbx/PbxIndex";
import CreditNotes from "views/CreditNotes/CreditNotesIndex";

import HasPermission from "hoc/RbacCTA";

import DebtorBasicDetail from "components/Debtor/Detail/BasicDetail/DebtorBasicDetail";
import DebtorAdditionalDetail from "components/Debtor/Detail/AdditionalDetail/AdditionalDetail";
import {
  TabWithRoute,
  TabRoute,
} from "components/Tab/TabWithRoute/TabWithRoute";
import FetchError from "components/Error/FetchError";
import DebtorDetailSkeleton from "components/Skeleton/ResourceDetail/DebtorDetail/DebtorDetailSkeleton";

import { CREDIT_NOTE } from "constants/routes";
import { RESOURCE_NAMES } from "constants/constant";

const DebtorDetail = ({
  match,
  fetchSingleDebtor,
  debtor,
  isLoading,
  history,
  location,
  fetchError,
}) => {
  const { params, url: baseUrl } = match;
  const { debtorID } = params;

  const fetchDebtor = () => {
    fetchSingleDebtor(debtorID);
  };

  useEffect(() => {
    fetchDebtor();
  }, [debtorID]);

  if (fetchError) {
    return <FetchError onRetry={fetchDebtor} />;
  }

  if (isLoading || !debtor) {
    return <DebtorDetailSkeleton />;
  }

  return (
    <HasPermission
      perform="debtors:view"
      data={{ debtorID: debtor.id || debtorID }}
      redirect
    >
      <div className="debtor-detail container-fluid w-100">
        <DebtorBasicDetail
          debtor={debtor}
          match={match}
          resourceName={RESOURCE_NAMES.DEBTOR}
        />
        <HasPermission
          perform="debtors:tabs"
          no={() => (
            <DebtorAdditionalDetail
              debtorID={debtorID}
              debtor={debtor}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />
          )}
        >
          <TabWithRoute match={match} redirectUrl="details">
            <TabRoute
              resourcePath="details"
              tabTitle={<Translate value="common.detail" />}
              render={
                <DebtorAdditionalDetail
                  debtorID={debtorID}
                  debtor={debtor}
                  resourceName={RESOURCE_NAMES.DEBTOR}
                />
              }
              baseUrl={baseUrl}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />

            <TabRoute
              resourcePath="contacts"
              tabTitle={<Translate value="menu.customerAdmin.contact" />}
              render={<ContactIndex history={history} parentID={debtorID} />}
              baseUrl={baseUrl}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />

            <TabRoute
              resourcePath="agreements"
              tabTitle={<Translate value="menuItem.agreement" />}
              render={<AgreementIndex parentID={debtorID} history={history} />}
              baseUrl={baseUrl}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />
            <TabRoute
              resourcePath={
                location.pathname.includes(CREDIT_NOTE.INDEX)
                  ? "credit_notes"
                  : "invoices"
              }
              tabTitle={
                location.pathname.includes(CREDIT_NOTE.INDEX) ? (
                  <Translate value="menuItem.creditNotes" />
                ) : (
                  <Translate value="menuItem.invoice" />
                )
              }
              render={
                location.pathname.includes(CREDIT_NOTE.INDEX) ? (
                  <CreditNotes parentID={debtorID} history={history} />
                ) : (
                  <InvoiceIndex parentID={debtorID} history={history} />
                )
              }
              baseUrl={baseUrl}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />
            <TabRoute
              resourcePath="pbxs"
              tabTitle={<Translate value="menuItem.pbx" />}
              render={<PbxIndex history={history} parentID={debtorID} />}
              baseUrl={baseUrl}
              resourceName={RESOURCE_NAMES.DEBTOR}
            />
          </TabWithRoute>
        </HasPermission>
      </div>
    </HasPermission>
  );
};

DebtorDetail.defaultProps = {
  isLoading: true,
  debtor: undefined,
  fetchError: null,
};

DebtorDetail.propTypes = {
  debtor: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customer_number: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    phone: PropTypes.string,
    status: PropTypes.string,
    address: PropTypes.string,
    website: PropTypes.string,
    created_at: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    params: PropTypes.shape({
      debtorID: PropTypes.string,
      vendorID: PropTypes.string,
    }),
  }).isRequired,
  fetchSingleDebtor: PropTypes.func.isRequired,
  fetchError: PropTypes.string,
};

const mapStateToProps = ({ debtor: debtorResource }) => {
  const { debtor, isLoading, fetchError } = debtorResource;

  return {
    debtor,
    isLoading,
    fetchError,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchSingleDebtor: (id) => {
    dispatch(singleDebtorFetchRequest(id));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(DebtorDetail);
